import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * Reference to the header-group element
   * @type {HTMLElement | null}
   */
  #headerGroup = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Minimum scroll distance (in px) to trigger hide when scrolling down
   * @constant {number}
   */
  #hideThreshold = 50;

  /**
   * Minimum scroll distance (in px) to trigger show when scrolling up
   * @constant {number}
   */
  #showThreshold = 3;

  /**
   * Cached height of header-group for performance
   * @type {number}
   */
  #headerGroupHeight = 0;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height, bottom } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);
    document.body.style.setProperty('--header-bottom', `${Math.max(0, bottom)}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    if (document.documentElement.hasAttribute('data-dialog-open')) {
      return;
    }

    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      const headerGroupRect = this.#headerGroup?.getBoundingClientRect();
      const isAtTop = headerGroupRect ? headerGroupRect.top >= 0 : true;
      
      const scrolledPastHeader = scrollTop > this.#headerGroupHeight;
      const scrollDelta = Math.abs(scrollTop - this.#lastScrollTop);

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (scrolledPastHeader) {
        if (isScrollingUp && scrollDelta >= this.#showThreshold) {
          this.dataset.scrollDirection = 'up';
        } else if (!isScrollingUp && scrollTop > (this.#headerGroupHeight + this.#hideThreshold)) {
          this.dataset.scrollDirection = 'down';
        }
      } else {
        this.dataset.scrollDirection = 'none';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  /**
   * Handles transition end event on header-group to update --header-bottom CSS variable
   * @param {TransitionEvent} event
   */
  #handleHeaderGroupTransitionEnd = (event) => {
    if (event.target === this.#headerGroup && event.propertyName === 'transform') {
      const headerBottom = this.getBoundingClientRect().bottom;
      document.body.style.setProperty('--header-bottom', `${Math.max(0, headerBottom)}px`);
    }
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    this.#headerGroup = document.querySelector('#header-group');
    if (this.#headerGroup) {
      this.#headerGroupHeight = this.#headerGroup.offsetHeight;
      
      new ResizeObserver(() => {
        if (this.#headerGroup) {
          this.#headerGroupHeight = this.#headerGroup.offsetHeight;
        }
      }).observe(this.#headerGroup);
    }

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }

      if (stickyMode === 'always' && this.#headerGroup) {
        this.#headerGroup.addEventListener('transitionend', this.#handleHeaderGroupTransitionEnd);
      }

      if (window.__scrollRestoreActive) {
        this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
        this.#offscreen = true;
        delete window.__scrollRestoreActive;
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#headerGroup) {
      this.#headerGroup.removeEventListener('transitionend', this.#handleHeaderGroupTransitionEnd);
    }
    document.body.style.setProperty('--header-height', '0px');
    document.body.style.setProperty('--header-bottom', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    // Calculate height of all non-header children (e.g. announcement bar) and expose as CSS variable
    const updateAnnouncementHeight = () => {
      const headerSection = header?.parentElement;
      let announcementHeight = 0;
      const groupChildren = headerGroup.children;
      for (let i = 0; i < groupChildren.length; i++) {
        const el = groupChildren[i];
        if (el === headerSection || el === header || !(el instanceof HTMLElement)) continue;
        announcementHeight += el.offsetHeight;
      }
      headerGroup.style.setProperty('--announcement-height', `${announcementHeight}px`);
    };

    const resizeObserver = new ResizeObserver(() => {
      const headerGroupHeight = calculateHeaderGroupHeight(header, headerGroup);
      document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
      
      // Also update header bottom as header group children changes might shift the header
      const headerBottom = header?.getBoundingClientRect().bottom;
      if (headerBottom !== undefined) {
        document.body.style.setProperty('--header-bottom', `${Math.max(0, headerBottom)}px`);
      }

      updateAnnouncementHeight();
    });

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Set initial announcement height
    updateAnnouncementHeight();

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
